import { FileSearch, PenTool, ScanSearch, FileCheck2 } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Eyebrow } from "@/components/Motion";
import { Seo } from "@/components/Seo";
import { whatsappWithText } from "@/lib/site";
import { WhatsAppIcon } from "@/components/WhatsAppButton";

const STEPS = [
  { icon: FileSearch, n: "01", title: "Fresh research, every order", text: "Your writer starts from your brief and current sources — never from a previous client's document, a template bank, or pre-written material." },
  { icon: PenTool, n: "02", title: "Written from a blank page", text: "Every sentence is composed for your specific project. Paraphrasing existing work or spinning old content is grounds for removal from our writing team." },
  { icon: ScanSearch, n: "03", title: "Similarity check before delivery", text: "Completed documents are run through plagiarism-detection software. Anything flagged is rewritten before you ever see it." },
  { icon: FileCheck2, n: "04", title: "Report delivered with your file", text: "You receive the similarity report alongside your document, so you can submit with evidence of originality in hand." },
];

export default function Guarantee() {
  return (
    <div data-testid="guarantee-page">
      <Seo
        title="Plagiarism-Free Guarantee | MBA Project & Thesis Writing Help"
        description="Every document is written from scratch, checked with plagiarism-detection software, and delivered with a similarity report. Free rework if your institution flags our writing."
      />
      <PageHero
        eyebrow="Our guarantee"
        lines={["100% original.", "Proven, not promised."]}
        sub="Plagiarism doesn't just cost marks — it can cost degrees. That's why originality isn't a feature of our service; it's the foundation of it."
      />

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 -mb-6">
        <Reveal className="max-w-3xl">
          <p className="text-base md:text-lg text-slate-600 leading-relaxed" data-testid="guarantee-plain-words">
            In plain words: when you order from us, nobody copies, nobody reuses, and nobody takes shortcuts. Your document is written for you alone, checked by software before it reaches you, and backed by a free rewrite if your university's checker ever flags our work. More than 1,500 delivered projects have been built on this one rule.
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <Reveal>
          <Eyebrow>How we guarantee it</Eyebrow>
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight max-w-2xl">
            Four safeguards on every single order.
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.07} className="border border-slate-200 rounded-2xl p-8 bg-white h-full" data-testid={`guarantee-step-${i}`}>
              <div className="flex items-center justify-between">
                <s.icon className="h-6 w-6 text-[#2563EB]" strokeWidth={1.5} />
                <span className="font-display font-extrabold text-4xl text-slate-100">{s.n}</span>
              </div>
              <h3 className="mt-5 font-display font-bold text-lg tracking-tight">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">{s.text}</p>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-16 border border-slate-200 rounded-2xl bg-[#F5F8FF] p-10 md:p-14">
          <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">What this means for you</h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-8 text-sm text-slate-600 leading-relaxed">
            <p>
              <strong className="text-[#1E2A78] font-semibold">No recycled work.</strong> Your document will never resemble another client's, because it never touches another client's material.
            </p>
            <p>
              <strong className="text-[#1E2A78] font-semibold">Evidence you can show.</strong> The similarity report we deliver is yours to keep — useful if your institution asks questions.
            </p>
            <p>
              <strong className="text-[#1E2A78] font-semibold">Free rework if flagged.</strong> If any section we wrote is flagged for similarity by your institution's checker, we rewrite it at no cost.
            </p>
          </div>
          <a
            href={whatsappWithText("Hi, I'd like to know more about your plagiarism-free guarantee.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="guarantee-whatsapp-cta"
            className="mt-10 inline-flex items-center gap-2.5 rounded-full bg-[#1E2A78] px-7 py-3.5 text-sm font-semibold text-white transition-[background-color,transform] duration-200 hover:bg-[#2563EB] hover:-translate-y-0.5"
          >
            <WhatsAppIcon className="h-5 w-5" /> Ask Us Anything
          </a>
        </Reveal>
      </section>
    </div>
  );
}
